import { useEffect, useRef, useState } from "react";
import { useLiveQuizSocket } from "../contexts/LiveQuizSocketContext";

export function useLiveQuizGame(roomCode) {
    const { socket, setActiveRoom } = useLiveQuizSocket();
    const timerRef = useRef(null);

    const [phase, setPhase] = useState("lobby");
    const [question, setQuestion] = useState(null);
    const [timeLeft, setTimeLeft] = useState(0);
    const [voteCounts, setVoteCounts] = useState([0, 0, 0, 0]);
    const [correctIndex, setCorrectIndex] = useState(null);
    const [myScore, setMyScore] = useState({ total: 0, delta: 0, isCorrect: null });
    const [leaderboard, setLeaderboard] = useState([]);
    const [players, setPlayers] = useState([]);
    const [hasAnswered, setHasAnswered] = useState(false);

    useEffect(() => {
        if (!socket) return;

        const doJoin = () => {
            socket.emit("join_room", { roomCode });
            setActiveRoom({ roomCode });
        };
        if (socket.connected) {
            doJoin();
        } else {
            socket.once("connect", doJoin);
        }

        socket.on("room_update", ({ players }) => setPlayers(players));

        socket.on("question_start", ({ index, total, question, options, timeLimit, timeLeft: tLeft }) => {
            clearInterval(timerRef.current);
            setPhase("question");
            setHasAnswered(false);
            setCorrectIndex(null);
            setVoteCounts([0, 0, 0, 0]);
            setQuestion({ index, total, text: question, options, timeLimit });
            const startTime = tLeft ?? timeLimit;
            setTimeLeft(startTime);

            timerRef.current = setInterval(() => {
                setTimeLeft(t => {
                    if (t <= 1) { clearInterval(timerRef.current); return 0; }
                    return t - 1;
                });
            }, 1000);
        });

        socket.on("question_result", ({ voteCounts, correctIndex }) => {
            clearInterval(timerRef.current);
            setPhase("result");
            setVoteCounts(voteCounts);
            setCorrectIndex(correctIndex);
        });

        socket.on("my_score_update", ({ delta, total, isCorrect }) => {
            setMyScore({ delta, total, isCorrect });
        });

        socket.on("leaderboard_update", ({ players }) => {
            setLeaderboard(players);
            setPhase("leaderboard");
        });

        socket.on("quiz_finished", ({ leaderboard }) => {
            setLeaderboard(leaderboard);
            setPhase("finished");
            setActiveRoom(null);
        });

        socket.on("teacher_left", () => {
            clearInterval(timerRef.current);
            setPhase("teacher_left");
            setActiveRoom(null);
        });

        socket.on("join_error", ({ message }) => {
            console.warn("[LiveQuiz] join_error:", message);
            setPhase("teacher_left");
        });

        socket.on("already_answered", () => {
            setHasAnswered(true);
        });

        socket.on("session_state", ({ phase: p, currentQuestion, questionIndex, players: p1 }) => {
            if (p1) setPlayers(p1);
            if (p === "question" && currentQuestion) {
                setPhase("question");
                setQuestion({
                    index: questionIndex,
                    total: currentQuestion.total || 0,
                    text: currentQuestion.question_text,
                    options: currentQuestion.options.map(o => o.answer_text),
                    timeLimit: currentQuestion.time_limit,
                });
                setTimeLeft(currentQuestion.timeLeft || currentQuestion.time_limit);
            } else if (p === "lobby") {
                setPhase("lobby");
            } else if (p === "result") {
                setPhase("result");
            } else if (p === "leaderboard") {
                setPhase("leaderboard");
            }
        });

        return () => {
            clearInterval(timerRef.current);
            socket.off("connect", doJoin);
            ["room_update", "question_start", "question_result", "my_score_update",
             "leaderboard_update", "quiz_finished", "teacher_left", "join_error", "already_answered", "session_state"]
                .forEach(e => socket.off(e));
        };
    }, [socket, roomCode]);

    const submitAnswer = (optionIndex) => {
        if (hasAnswered || !socket) return;
        setHasAnswered(true);
        socket.emit("submit_answer", { roomCode, optionIndex });
    };

    return {
        phase, question, timeLeft, voteCounts, correctIndex,
        myScore, leaderboard, players, hasAnswered, submitAnswer,
    };
}
